<template>
  <div class="alarm-module">
    <div class="module-title">
      <i class="el-icon-warning"></i>
      最新预警信息
    </div>
    
    <!-- 最新5条固定显示 -->
    <div v-if="topAlarms.length > 0" class="alarm-list fixed-list">
      <div 
        v-for="(alarm, index) in topAlarms" 
        :key="`top-${alarm.machineCode}-${alarm.alarmTime}-${index}`"
        class="alarm-item"
        @click="showAlarmDetail(alarm)"
      >
        <div class="alarm-header">
          <span class="machine-label">{{ alarm.machineName }}</span>
          <span class="alarm-time">{{ formatTime(alarm.alarmTime) }}</span>
        </div>
        <div class="alarm-content">
          <span class="alarm-text">{{ alarm.alarmContent }}</span>
        </div>
      </div>
    </div>
    
    <!-- 分隔线 -->
    <div v-if="topAlarms.length > 0 && scrollAlarms.length > 0" class="divider">
      <span>历史预警</span>
    </div>
    
    <!-- 其余预警滚动显示 -->
    <div v-if="scrollAlarms.length > 0" class="alarm-list scroll-list" ref="scrollList">
      <div 
        v-for="(alarm, index) in scrollAlarms" 
        :key="`scroll-${alarm.machineCode}-${alarm.alarmTime}-${index}`"
        class="alarm-item"
        @click="showAlarmDetail(alarm)"
      >
        <div class="alarm-header">
          <span class="machine-label">{{ alarm.machineName }}</span>
          <span class="alarm-time">{{ formatTime(alarm.alarmTime) }}</span>
        </div>
        <div class="alarm-content">
          <span class="alarm-text">{{ alarm.alarmContent }}</span>
        </div>
      </div>
    </div>
    
    <div v-if="allAlarms.length === 0" class="no-alarm">
      <i class="el-icon-success"></i>
      <span>暂无预警信息</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AlarmModule',
  props: {
    machines: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      scrollTimer: null
    }
  },
  computed: {
    allAlarms() {
      // 收集所有机器的报警信息
      const alarms = []
      this.machines.forEach(machine => {
        if (machine.alarms && machine.alarms.length > 0) {
          machine.alarms.forEach(alarm => {
            alarms.push({
              ...alarm,
              machineCode: machine.machineCode,
              machineName: machine.machineName
            })
          })
        }
      })
      
      // 按时间戳降序排序
      return alarms.sort((a, b) => {
        const timeA = new Date(a.alarmTime).getTime()
        const timeB = new Date(b.alarmTime).getTime()
        return timeB - timeA
      })
    },
    
    topAlarms() {
      // 最新3条固定显示
      return this.allAlarms.slice(0, 3)
    },
    
    scrollAlarms() {
      // 第4条以后的所有数据滚动显示
      return this.allAlarms.slice(3)
    }
  },
  watch: {
    scrollAlarms(val) {
      // 先停止旧的定时器，避免重复启动
      this.stopScroll()
      
      // 有滚动区域的预警时启动自动滚动
      if (val && val.length > 0) {
        this.$nextTick(() => {
          this.startScroll()
        })
      }
    }
  },
  mounted() {
    if (this.scrollAlarms.length > 0) {
      this.$nextTick(() => {
        this.startScroll()
      })
    }
  },
  beforeDestroy() {
    this.stopScroll()
  },
  methods: {
    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    
    showAlarmDetail(alarm) {
      this.$alert(
        `<div style="text-align: left;">
          <p><strong>设备:</strong> ${alarm.machineName}</p>
          <p><strong>时间:</strong> ${this.formatTime(alarm.alarmTime)}</p>
          <p><strong>内容:</strong> ${alarm.alarmContent}</p>
        </div>`,
        '预警详情',
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: '确定'
        }
      )
    },
    
    startScroll() {
      // 先清除旧的定时器，确保只有一个定时器在运行
      this.stopScroll()
      
      // 只滚动下方的滚动区域，降低滚动速度
      this.scrollTimer = setInterval(() => {
        const container = this.$refs.scrollList
        if (!container) return
        
        // 平滑滚动，每次滚动0.5像素，速度更慢
        if (container.scrollTop >= container.scrollHeight - container.clientHeight) {
          container.scrollTop = 0
        } else {
          container.scrollTop += 0.5
        }
      }, 50) // 每50ms滚动0.5像素，相当于每秒10像素
    },
    
    stopScroll() {
      if (this.scrollTimer) {
        clearInterval(this.scrollTimer)
        this.scrollTimer = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.alarm-module {
  background: rgba(10, 20, 40, 0.6);
  border: 1px solid rgba(255, 255, 0, 0.3);
  border-radius: 8px;
  padding: 15px;
  backdrop-filter: blur(10px);
  box-shadow: 0 0 20px rgba(255, 255, 0, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.module-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ffff00;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 15px;
  text-shadow: 0 0 10px rgba(255, 255, 0, 0.5);
  flex-shrink: 0;
  
  i {
    font-size: 18px;
    animation: warning-pulse 1.5s ease-in-out infinite;
  }
}

@keyframes warning-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}

.alarm-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  
  &.fixed-list {
    // 固定区域不滚动
    flex-shrink: 0;
  }
  
  &.scroll-list {
    // 滚动区域，自适应剩余空间
    flex: 1;
    overflow-y: auto;
    
    &::-webkit-scrollbar {
      width: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 0, 0.3);
      border-radius: 2px;
    }
  }
}

.divider {
  margin: 10px 0;
  padding: 8px 0;
  text-align: center;
  border-top: 1px solid rgba(255, 255, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 0, 0.2);
  
  span {
    color: rgba(255, 255, 0, 0.6);
    font-size: 12px;
    letter-spacing: 1px;
  }
}

.alarm-item {
  background: rgba(255, 0, 0, 0.1);
  border-left: 3px solid #ff4444;
  border-radius: 4px;
  padding: 8px 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(255, 0, 0, 0.2);
    transform: translateX(5px);
    box-shadow: 0 0 15px rgba(255, 68, 68, 0.3);
  }
}

.alarm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.machine-label {
  color: #ffff00;
  font-size: 13px;
  font-weight: bold;
  text-shadow: 0 0 5px rgba(255, 255, 0, 0.3);
}

.alarm-time {
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  flex-shrink: 0;
  margin-left: 8px;
}

.alarm-content {
  display: flex;
  flex-direction: column;
}

.alarm-text {
  color: #ff4444;
  font-size: 13px;
  line-height: 1.4;
  text-shadow: 0 0 5px rgba(255, 68, 68, 0.3);
  word-break: break-word;
}

.no-alarm {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.5);
  
  i {
    font-size: 48px;
    color: #00ff00;
  }
  
  span {
    font-size: 14px;
  }
}
</style>
